import * as Yup from 'yup';
import { InferType } from 'yup';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFTextField } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import { Button, Typography, DialogTitle, DialogActions, DialogContent } from '@mui/material';

import { useAddFavouritesCollection } from 'src/api/hooks/api-hooks';

interface CreatePersonalFavouritesCollectionFormProps {
  onBack: () => void;
  onSuccess?: (collectionId: string) => void;
}

export default function CreatePersonalFavouritesCollectionForm({
  onBack,
  onSuccess,
}: CreatePersonalFavouritesCollectionFormProps) {
  const { mutate: addFavouritesCollection, isLoading: isCreatingCollection } =
    useAddFavouritesCollection();
  const queryClient = useQueryClient();

  const AddFavouritesListSchema = Yup.object().shape({
    name: Yup.string()
      .max(100, 'Favourites list name must be shorter than 100 characters')
      .required('List Name is required'),
  });

  const defaultValues = useMemo(
    () => ({
      name: '',
    }),
    []
  );

  type ValidationSchemaType = InferType<typeof AddFavouritesListSchema>;

  const methods = useForm<ValidationSchemaType>({
    resolver: yupResolver(AddFavouritesListSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data: ValidationSchemaType) => {
    addFavouritesCollection(
      {
        body: data,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Favourites List Added Successfully', { variant: 'success' });
          queryClient.invalidateQueries(['favourites']).then(() => {
            const updatedCollections = queryClient.getQueryData(['favourites']) as any;

            let newCollectionId: string | null = null;
            if (updatedCollections?.personal) {
              const newCollection = updatedCollections.personal.find(
                (col: any) => col.name === data.name
              );
              if (newCollection) {
                newCollectionId = newCollection.id;
              }
            }

            if (newCollectionId && onSuccess) {
              onSuccess(newCollectionId);
            }

            reset();
            onBack();
          });
        },
      }
    );
  });

  const handleBack = () => {
    reset();
    onBack();
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <DialogTitle>Create New Favourites List</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Group your favourite products into useful lists to access regularly.
        </Typography>
        <RHFTextField name="name" label="List Name" />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={isSubmitting || isCreatingCollection}
        >
          Create List
        </LoadingButton>
      </DialogActions>
    </FormProvider>
  );
}
